import { DEFAULT_LOCALE, LOCALE_PREFERENCE_KEY, type Locale } from '@/constant/locale'
import type { LocaleLabelKey } from '@/constant/locale-labels'
import { getLocaleLabel } from '@/constant/locale-labels'
import { useUserPreference } from '@/hooks/use-user-preference'

/**
 * Hook for locale-aware labels (e.g. Australian vs US English spelling).
 * Use t('key') to get the label for the current user locale.
 *
 * @example
 * const { locale, t } = useLocale()
 * "Email " + t('customisation') // "Email Customisation" (en-AU) or "Email Customization" (en-US)
 */
export const useLocale = () => {
  const { userPreferences, upsertUserPreference } = useUserPreference()

  const locale: Locale =
    (userPreferences?.find((p) => p.key === LOCALE_PREFERENCE_KEY)?.value as Locale) || DEFAULT_LOCALE

  const setLocale = async (newLocale: Locale) => {
    await upsertUserPreference(LOCALE_PREFERENCE_KEY, newLocale, true)
  }

  const t = (key: LocaleLabelKey): string => getLocaleLabel(key, locale)

  return { locale, setLocale, t }
}
